'use client';

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { SHOPIFY_CONFIG } from '@/data/store';
import { COLORS } from '@/constants/colors';

interface UnifiedProductProps {
  buyButtonId: string;
  id: string;
}

declare global {
  interface Window {
    ShopifyBuy?: any;
  }
}

const UnifiedProduct: React.FC<UnifiedProductProps> = ({ buyButtonId, id }) => {
  const prevBuyButtonIdRef = useRef<string | undefined>(undefined);
  const componentRef = useRef<any>(null);

  useEffect(() => {
    const buyButtonChanged = prevBuyButtonIdRef.current?.toString() !== buyButtonId?.toString();

    if (buyButtonId && buyButtonChanged) {
      prevBuyButtonIdRef.current = buyButtonId;

      const scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

      function ShopifyBuyInit() {
        if (!window.ShopifyBuy) return;

        const client = window.ShopifyBuy.buildClient({
          domain: SHOPIFY_CONFIG.domain,
          apiKey: SHOPIFY_CONFIG.apiKey,
          appId: SHOPIFY_CONFIG.appId,
        });

        window.ShopifyBuy.UI.onReady(client).then(function (ui: any) {
          const node = document.getElementById(`unified-product-${id}`);
          if (!node) return;

          // Destroy any existing components first
          if (componentRef.current) {
            try {
              componentRef.current.destroy();
            } catch (e) {
              console.warn('Failed to destroy component:', e);
            }
          }

          // Clear existing content
          node.innerHTML = '';

          const component = ui.createComponent('product', {
            id: buyButtonId,
            node: node,
            moneyFormat: '%24%7B%7Bamount%7D%7D',
            options: {
              product: {
                layout: 'vertical',
                width: '100%',
                contents: {
                  img: false,
                  imgWithCarousel: true,
                  title: false,
                  variantTitle: false,
                  price: true,
                  description: false,
                  options: true,
                  buttonWithQuantity: false,
                  quantity: false,
                  button: true,
                },
                styles: {
                  product: {
                    'text-align': 'center !important',
                    'max-width': '100% !important',
                    'margin': '0 !important',
                    'padding': '0 !important',
                  },
                  img: {
                    'max-width': '100% !important',
                    'max-height': '450px !important',
                    'width': 'auto !important',
                    'height': 'auto !important',
                    'object-fit': 'contain !important',
                    'margin': '0 auto !important',
                    'display': 'block !important',
                  },
                  imgWrapper: {
                    'background': '#f8f8f8 !important',
                    'border-radius': '8px !important',
                    'padding': '32px !important',
                    'min-height': '350px !important',
                    'display': 'flex !important',
                    'flex-direction': 'column !important',
                    'align-items': 'center !important',
                    'justify-content': 'center !important',
                    'margin-bottom': '24px !important',
                    'border': '1px solid #e0e0e0 !important',
                  },
                  carousel: {
                    'display': 'flex !important',
                    'gap': '10px !important',
                    'margin-top': '16px !important',
                    'justify-content': 'center !important',
                    'flex-wrap': 'wrap !important',
                    'width': '100% !important',
                  },
                  carouselItem: {
                    'width': '60px !important',
                    'height': '60px !important',
                    'border': '2px solid #d0d0d0 !important',
                    'border-radius': '4px !important',
                    'cursor': 'pointer !important',
                    'transition': 'all 0.2s ease !important',
                    'overflow': 'hidden !important',
                    'background-color': '#fff !important',
                    'background-size': 'contain !important',
                    'background-position': 'center !important',
                    'background-repeat': 'no-repeat !important',
                    'padding': '3px !important',
                    ':hover': {
                      'border-color': `${COLORS.brandGreen} !important`,
                      'box-shadow': '0 2px 8px rgba(0, 0, 0, 0.12) !important',
                    },
                  },
                  carouselItemSelected: {
                    'border-color': `${COLORS.brandGreen} !important`,
                    'box-shadow': '0 0 0 2px rgba(0, 173, 35, 0.15) !important',
                  },
                  carouselItemImage: {
                    'width': '100% !important',
                    'height': '100% !important',
                    'object-fit': 'contain !important',
                    'display': 'block !important',
                  },
                  option: {
                    'margin-bottom': '0 !important',
                  },
                  variantLabel: {
                    'font-family': 'Lato, sans-serif !important',
                    'font-size': '13px !important',
                    'font-weight': '600 !important',
                    'color': '#525252 !important',
                    'text-transform': 'uppercase !important',
                    'letter-spacing': '0.5px !important',
                    'margin-bottom': '8px !important',
                    'display': 'block !important',
                  },
                  select: {
                    'font-family': 'Lato, sans-serif !important',
                    'font-size': '14px !important',
                    'font-weight': '500 !important',
                    'color': '#313131 !important',
                    'background-color': '#fff !important',
                    'border': '2px solid #e8e8e8 !important',
                    'border-radius': '6px !important',
                    'padding': '12px 40px 12px 14px !important',
                    'cursor': 'pointer !important',
                    'transition': 'all 0.2s ease !important',
                    'appearance': 'none !important',
                    '-webkit-appearance': 'none !important',
                    '-moz-appearance': 'none !important',
                    'width': '100% !important',
                    'box-shadow': 'none !important',
                    'background-image': 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2300ad23\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e") !important',
                    'background-repeat': 'no-repeat !important',
                    'background-position': 'right 12px center !important',
                    'background-size': '18px !important',
                    'margin-bottom': '16px !important',
                    ':hover': {
                      'border-color': `${COLORS.brandGreen} !important`,
                      'background-color': '#f9f9f9 !important',
                    },
                    ':focus': {
                      'outline': 'none !important',
                      'border-color': `${COLORS.brandGreen} !important`,
                      'box-shadow': '0 0 0 3px rgba(0, 173, 35, 0.1) !important',
                    },
                  },
                  selectWrapper: {
                    'margin-bottom': '0 !important',
                  },
                  price: {
                    'font-family': 'Lato, sans-serif !important',
                    'font-size': '38px !important',
                    'font-weight': '800 !important',
                    'color': `${COLORS.brandGreen} !important`,
                    'line-height': '1.2 !important',
                    'margin-bottom': '20px !important',
                  },
                  compareAt: {
                    'font-family': 'Lato, sans-serif !important',
                    'font-size': '24px !important',
                    'color': '#999 !important',
                    'text-decoration': 'line-through !important',
                    'font-weight': '400 !important',
                    'margin-left': '12px !important',
                  },
                  button: {
                    'font-family': 'Lato, sans-serif !important',
                    'font-size': '13px !important',
                    'font-weight': '700 !important',
                    'text-transform': 'uppercase !important',
                    'letter-spacing': '0.8px !important',
                    'padding': '16px 32px !important',
                    'background': `${COLORS.brandGreen} !important`,
                    'color': '#fff !important',
                    'border-radius': '4px !important',
                    'border': 'none !important',
                    'box-shadow': '0 2px 8px rgba(0, 173, 35, 0.2) !important',
                    'transition': 'all 0.3s ease !important',
                    'cursor': 'pointer !important',
                    'width': '100% !important',
                    'min-height': '48px !important',
                    ':hover': {
                      'background': '#009c1f !important',
                      'box-shadow': '0 4px 12px rgba(0, 173, 35, 0.3) !important',
                      'transform': 'translateY(-1px) !important',
                    },
                    ':active': {
                      'background': '#008a1c !important',
                      'transform': 'translateY(0) !important',
                      'box-shadow': '0 2px 6px rgba(0, 173, 35, 0.2) !important',
                    },
                    ':focus': {
                      'outline': 'none !important',
                      'box-shadow': '0 0 0 3px rgba(0, 173, 35, 0.2) !important',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
              cart: {
                styles: {
                  button: {
                    'font-family': 'Lato, sans-serif !important',
                    'background-color': `${COLORS.brandGreen} !important`,
                    ':hover': {
                      'background-color': '#009c1f !important',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
            },
          });

          // Store component reference
          componentRef.current = component;

          // Hide all carousel elements if only one image exists
          setTimeout(() => {
            const productContainer = document.querySelector(`#unified-product-${id}`) as HTMLElement;
            if (productContainer) {
              // Count carousel items
              const carouselItems = productContainer.querySelectorAll('.shopify-buy__carousel-item, [data-element="carousel.item"]');

              if (carouselItems.length <= 1) {
                // Add class to container for CSS targeting
                productContainer.classList.add('single-image-product');

                // Hide carousel thumbnails
                const carousel = productContainer.querySelector('.shopify-buy__carousel');
                if (carousel) {
                  (carousel as HTMLElement).style.display = 'none !important';
                }

                // Hide ALL navigation arrows with multiple selectors
                const navSelectors = [
                  '.shopify-buy__carousel-button--previous',
                  '.shopify-buy__carousel-button--next',
                  '[data-element="carousel.button.previous"]',
                  '[data-element="carousel.button.next"]',
                  'button[aria-label*="Previous"]',
                  'button[aria-label*="Next"]',
                  '.shopify-buy__carousel-button',
                ];

                navSelectors.forEach(selector => {
                  productContainer.querySelectorAll(selector).forEach(el => {
                    (el as HTMLElement).style.display = 'none';
                    (el as HTMLElement).style.visibility = 'hidden';
                    (el as HTMLElement).style.opacity = '0';
                    (el as HTMLElement).style.pointerEvents = 'none';
                  });
                });

                // Find and hide parent containers that might contain arrows
                const imgWithCarousel = productContainer.querySelector('[data-element="product.imgWithCarousel"]');
                if (imgWithCarousel) {
                  imgWithCarousel.querySelectorAll('button').forEach(button => {
                    const ariaLabel = button.getAttribute('aria-label') || '';
                    if (ariaLabel.toLowerCase().includes('previous') ||
                        ariaLabel.toLowerCase().includes('next') ||
                        button.className.includes('carousel')) {
                      (button as HTMLElement).style.display = 'none';
                    }
                  });
                }
              }
            }
          }, 800);
        });
      }

      function loadScript() {
        const script = document.createElement('script');
        script.async = true;
        script.src = scriptURL;
        (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
        script.onload = ShopifyBuyInit;
      }

      if (window.ShopifyBuy) {
        if (window.ShopifyBuy.UI) {
          ShopifyBuyInit();
        } else {
          loadScript();
        }
      } else {
        loadScript();
      }
    }

    // Cleanup
    return () => {
      if (componentRef.current) {
        try {
          componentRef.current.destroy();
        } catch (e) {
          // Component already destroyed
        }
      }
    };
  }, [buyButtonId, id]);

  return (
    <Container>
      <div id={`unified-product-${id}`} />
    </Container>
  );
};

export default UnifiedProduct;

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  /* Ensure carousel displays below image */
  .shopify-buy__carousel {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 10px !important;
    justify-content: center !important;
    margin-top: 16px !important;
  }

  /* Hide carousel when only one image exists */
  .shopify-buy__carousel:has(> :only-child),
  .shopify-buy__carousel > :only-child {
    display: none !important;
  }

  /* Hide carousel arrows when only one item */
  .shopify-buy__carousel:has(> :only-child) ~ .shopify-buy__carousel-button--previous,
  .shopify-buy__carousel:has(> :only-child) ~ .shopify-buy__carousel-button--next,
  [data-element="product.imgWithCarousel"]:has(.shopify-buy__carousel > :only-child) .shopify-buy__carousel-button--previous,
  [data-element="product.imgWithCarousel"]:has(.shopify-buy__carousel > :only-child) .shopify-buy__carousel-button--next,
  [data-element="product.imgWithCarousel"]:has(.shopify-buy__carousel > :only-child) [data-element="carousel.button.previous"],
  [data-element="product.imgWithCarousel"]:has(.shopify-buy__carousel > :only-child) [data-element="carousel.button.next"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }

  /* Make carousel item images visible with multiple selectors */
  .shopify-buy__carousel-item img,
  .shopify-buy__carousel button img,
  .shopify-buy__carousel-item__image,
  [data-element="carousel.item"] img {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  /* Ensure carousel items show images */
  .shopify-buy__carousel button {
    padding: 3px !important;
    background: #fff !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  /* Hide all carousel-related navigation elements with single image */
  &:has(.shopify-buy__carousel > :only-child) {
    .shopify-buy__carousel-button,
    [class*="carousel-button"],
    [data-element*="carousel.button"] {
      display: none !important;
    }
  }

  /* When marked as single-image product, hide all carousel UI */
  &.single-image-product {
    .shopify-buy__carousel,
    .shopify-buy__carousel-item,
    .shopify-buy__carousel-button,
    .shopify-buy__carousel-button--previous,
    .shopify-buy__carousel-button--next,
    [data-element="carousel"],
    [data-element="carousel.item"],
    [data-element="carousel.button.previous"],
    [data-element="carousel.button.next"],
    [class*="carousel-button"],
    button[aria-label*="Previous"],
    button[aria-label*="Next"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
`;
